// Direction 1 layout styles for the full-page Log Job (2026-09-08). Everything
// NEW about the per-device frame lives here so logJobStyles.js (rows, cards,
// the bottom bar chrome) stays under the line cap. Tokens only — no literal
// that exists as a token.
import { foundation } from '../ui';
import { shellHeaderBar } from '../ui/dataSurfaceStyles';
import { shellHeaderChrome, cssMobileShellHeaderPaddingTop } from '../ui/shellIdentityStyles';

const fc = foundation.color;
const sp = foundation.spacePx;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;
const rd = foundation.radii;

/** Max width of the form column (tablet) AND the bottom bar's inner row. */
export const LOGJOB_COLUMN_MAX_WIDTH = 680;
/** Desktop form column inside the workspace (wider than tablet's — Option B). */
export const LOGJOB_COLUMN_MAX_WIDTH_DESKTOP = 880;
/** Desktop summary rail width. */
export const LOGJOB_RAIL_WIDTH = 360;
/** Desktop two-pane workspace, centered (Option B). */
export const LOGJOB_WORKSPACE_MAX_WIDTH = 1280;

export const layoutStyles = {
  // ── Phone: compact context header ────────────────────────────────────────
  compactHeader: {
    ...shellHeaderChrome,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: sp[2],
    paddingTop: cssMobileShellHeaderPaddingTop(),
    paddingRight: sp[3],
    paddingBottom: sp[2],
    paddingLeft: sp[2],
    minHeight: '52px',
    background: fc.neutral.surface,
    borderBottom: `1px solid ${fc.neutral.border}`,
  },
  compactHeaderBack: {
    width: '44px',
    height: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: fc.brand.text,
    borderRadius: rd.md,
    cursor: 'pointer',
    flexShrink: 0,
  },
  compactHeaderText: { minWidth: 0, flex: 1, lineHeight: 1.2 },
  compactHeaderTitle: {
    fontSize: fs.base,
    fontWeight: fw.bold,
    color: fc.neutral.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  compactHeaderSub: { fontSize: fs.xs, color: fc.neutral.textMuted, marginTop: '2px' },

  // ── Tablet + desktop: breadcrumb bar ─────────────────────────────────────
  crumbBar: {
    ...shellHeaderBar,
    ...shellHeaderChrome,
    borderBottom: `1px solid ${fc.neutral.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: sp[4],
    minHeight: '48px',
    paddingLeft: sp[6],
    paddingRight: sp[6],
    flexShrink: 0,
    zIndex: 100,
  },
  crumbText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: sp[2],
    fontSize: fs.sm,
    color: fc.neutral.textMuted,
    minWidth: 0,
  },
  crumbRoot: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    font: 'inherit',
    fontWeight: fw.semibold,
    color: fc.neutral.text,
    cursor: 'pointer',
  },
  crumbSep: { color: fc.neutral.textMuted },
  crumbName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  crumbBackButton: { minHeight: '38px' },

  // ── Phone: section jump strip ────────────────────────────────────────────
  // Deliberately NOT sticky. The strip is a flex SIBLING of the scroll
  // container (LogJob.js renders header / strip / scroller / bar as children
  // of `styles.container`, which is `height:100dvh; overflow:hidden`), so only
  // the scroller ever scrolls and the strip is already permanently on screen.
  // A `position:sticky` here was added on 2026-09-11 against an audit finding
  // that claimed the strip "scrolls away on the first swipe" — it does not,
  // and the declaration was inert, along with a `top` whose arithmetic could
  // never fire. Do not re-add it without first moving the strip INSIDE the
  // scroller, which is the only arrangement that would need it.
  jumpStrip: {
    display: 'flex',
    gap: sp[1],
    overflowX: 'auto',
    scrollbarWidth: 'none',
    padding: `${sp[2]} ${sp[3]}`,
    background: fc.neutral.surface,
    borderBottom: `1px solid ${fc.neutral.border}`,
    WebkitOverflowScrolling: 'touch',
  },
  jumpPill: {
    flexShrink: 0,
    border: `1px solid ${fc.neutral.border}`,
    background: fc.neutral.surface,
    color: fc.neutral.text,
    borderRadius: rd.full,
    padding: `${sp[1]} ${sp[3]}`,
    fontSize: fs.xs,
    fontWeight: fw.semibold,
    minHeight: '32px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  jumpPillActive: {
    background: fc.brand.primary,
    borderColor: fc.brand.primary,
    color: fc.brand.onPrimary,
  },

  // ── Phone: context strip (replaces the hero card) ─────────────────────────
  contextStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: sp[2],
    flexWrap: 'wrap',
    padding: `${sp[1]} 0 ${sp[2]}`,
  },
  contextStripAddress: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: fs.sm,
    color: fc.neutral.textMuted,
    minWidth: 0,
  },

  // ── Desktop: two panes ───────────────────────────────────────────────────
  twoPane: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    width: '100%',
    // KAN-785: the workspace fills the screen so the rail pins to the right
    // edge; the form column (maxWidth + auto margins) centers in the rest.
  },
  formPane: { flex: 1, minWidth: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' },
  column: { maxWidth: `${LOGJOB_COLUMN_MAX_WIDTH}px`, margin: '0 auto', width: '100%' },
  columnDesktop: { maxWidth: `${LOGJOB_COLUMN_MAX_WIDTH_DESKTOP}px` },
  columnPadTablet: { padding: `${sp[6]} ${sp[6]} ${sp[8]}` },
  columnPadDesktop: { padding: `${sp[7]} ${sp[6]} ${sp[8]}` },
  // The rail is a FRAME, not a scroller (RD-UI-RAILPIN-001, KAN-1179): a
  // fixed-height column that clips, holding ONE scroll region (`railScroll`)
  // and, after it, the submit block as a plain flex footer. The block can
  // never move while the rail scrolls — it is not inside the scroller at all
  // (a sticky block inside the scroller rode up ~28px with a blank strip under
  // it at the scroll end on desktop Safari).
  rail: {
    width: `${LOGJOB_RAIL_WIDTH}px`,
    flex: '0 0 auto',
    minHeight: 0,
    overflow: 'hidden',
    borderLeft: `1px solid ${fc.neutral.border}`,
    background: fc.neutral.surface,
    display: 'flex',
    flexDirection: 'column',
  },
  // The rail's one scroller: identity + cards. Ends exactly at the footer's
  // top edge, so content scrolls cleanly behind it (the curtain).
  railScroll: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'column',
    gap: sp[3],
    padding: sp[4],
  },
  railCard: {
    background: fc.neutral.surface,
    border: `1px solid ${fc.neutral.border}`,
    borderRadius: rd.lg,
    padding: `${sp[2]} ${sp[3]}`,
  },
  railRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: sp[2],
    padding: `${sp[2]} 0`,
    fontSize: fs.sm,
    color: fc.neutral.text,
    borderBottom: `1px solid ${fc.neutral.border}`,
  },
  railRowLabel: { color: fc.neutral.textMuted },
  railLastVisitLine: { fontSize: fs.sm, color: fc.neutral.text, marginTop: '4px' },
  // The rail's footer: the ONLY total, plus Submit. A flex sibling AFTER the
  // scroll region (`rail` > `railScroll` + this), `flexShrink: 0`, so it sits
  // flush at the rail's bottom whether the rail is short or tall and never
  // moves with the scroll. The designed 24px under the primary lives INSIDE
  // the block (8 + the 16 the rail used to pad below it); the side inset is its
  // own margin. `background` + `borderTop` are the curtain line.
  // NOTE: render it as the rail's LAST child, never inside `railScroll`.
  railSubmitBlock: {
    flexShrink: 0,
    margin: `0 ${sp[4]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: sp[2],
    background: fc.neutral.surface,
    borderTop: `1px solid ${fc.neutral.border}`,
    paddingTop: sp[3],
    paddingBottom: `calc(${sp[2]} + ${sp[4]})`,
  },

  /** RD-ROLE-005 (worker price-blind) — the Extras section footnote shown only
   * when the role can't see prices AND at least one extra exists ("Extras are
   * priced by the office."). */
  extrasFootnote: { fontSize: fs.xs, color: fc.neutral.textMuted, margin: '4px 0 0 0' },

  // ── Bottom bar inner row (tablet holds it to the column; phone is full) ──
  // LogJobSubmitBar's `bar` variant overrides flexDirection to 'column' for
  // its two-row layout; the maxWidth cap here is what keeps the tablet bar
  // aligned with the form column above it.
  barInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: sp[4],
    width: '100%',
    maxWidth: `${LOGJOB_COLUMN_MAX_WIDTH}px`,
    margin: '0 auto',
  },
  barInnerFull: { maxWidth: 'none' },
};

export default layoutStyles;
